import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import memberService from '../../services/memberService';
import './AddEditModal.css';

const AddEditModal = ({ type, parent, editingItem, onClose, onSave }) => {
  const { t } = useTranslation();
  const autocompleteRef = useRef(null);
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    managerId: editingItem?.manager?.id || '',
    managerName: editingItem?.manager_name || '',
    description: editingItem?.description || ''
  });
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [managerSearch, setManagerSearch] = useState(
    editingItem?.manager 
      ? `${editingItem.manager.chi_name} (${editingItem.manager.worship_id})` 
      : (editingItem?.manager_name || '')
  );
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);
        const data = await memberService.getMembers({ limit: 1000 });
        setMembers(data.data || []);
      } catch (err) {
        console.error('Error loading members:', err);
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const typeNames = {
    district: {
      label: t('organization.types.district'),
      manager: t('organization.types.districtManager')
    },
    zone: {
      label: t('organization.types.zone'),
      manager: t('organization.types.zoneManager')
    },
    group: {
      label: t('organization.types.group'),
      manager: t('organization.types.groupManager')
    }
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    const searchLower = managerSearch.toLowerCase();
    return (
      member.chi_name?.toLowerCase().includes(searchLower) ||
      member.worship_id?.toLowerCase().includes(searchLower) ||
      member.eng_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelectManager = (member) => {
    setFormData({ ...formData, managerId: member.id.toString(), managerName: '' });
    setManagerSearch(`${member.chi_name} (${member.worship_id})`);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setManagerSearch(value);
    // If user is typing a custom name (not selecting from list), store it as managerName
    setFormData({ ...formData, managerId: '', managerName: value });
    setShowDropdown(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingItem ? t('organization.modal.editTitle') : t('organization.modal.createTitle')}{typeNames[type].label}</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {parent && (
              <div className="form-group">
                <label>{t('organization.modal.parentLabel', { parent: typeNames[parent.type].label })}</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={parent.name} 
                  disabled 
                />
              </div>
            )}
            <div className="form-group">
              <label>{t('organization.modal.nameLabel', { type: typeNames[type].label })}</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder={t('organization.modal.namePlaceholder', { type: typeNames[type].label })}
              />
            </div>
            <div className="form-group">
              <label>{t('organization.modal.managerLabel', { manager: typeNames[type].manager })}</label>
              <div className="autocomplete-container" ref={autocompleteRef}>
                <input
                  type="text"
                  className="form-control"
                  value={managerSearch}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={loadingMembers ? t('organization.modal.loadingMembers') : t('organization.modal.searchManager', { manager: typeNames[type].manager })}
                  disabled={loadingMembers}
                  required={!formData.managerId && !formData.managerName}
                />
                <p className="form-hint text-muted text-sm mt-1">{t('organization.modal.managerHint')}</p>
                {showDropdown && managerSearch && filteredMembers.length > 0 && (
                  <div className="autocomplete-dropdown">
                    {filteredMembers.slice(0, 10).map(member => (
                      <div
                        key={member.id}
                        className="autocomplete-item"
                        onClick={() => handleSelectManager(member)}
                      >
                        <span className="autocomplete-name">{member.chi_name}</span>
                        <span className="autocomplete-id">({member.worship_id})</span>
                      </div>
                    ))}
                    {filteredMembers.length > 10 && (
                      <div className="autocomplete-more">
                        {t('organization.modal.moreMembers', { count: filteredMembers.length - 10 })}
                      </div>
                    )}
                  </div>
                )}
                {showDropdown && managerSearch && filteredMembers.length === 0 && !loadingMembers && (
                  <div className="autocomplete-dropdown">
                    <div className="autocomplete-empty">{t('organization.modal.noMembers')}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>{t('organization.modal.description')}</label>
              <textarea
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder={t('organization.modal.descriptionPlaceholder')}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('organization.modal.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-save"></i> {t('organization.modal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditModal;
